'use client';

import { Suspense, useState, type FormEvent } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import Link from 'next/link';
import { authClient } from '@/lib/auth-client';
import { Button } from '@/components/ui/Button';
import { TextField } from '@/components/ui/TextField';
import { FormMessage } from '@/components/ui/FormMessage';
import { Card } from '@/components/ui/Card';

// S-1.3: single-use, short-lived reset tokens (auth.ts,
// resetPasswordTokenExpiresIn: 1 hour). This page serves both steps —
// the emailed link carries ?token=, so there is no separate request vs.
// confirm route.
function ResetPasswordForm() {
  const router = useRouter();
  const token = useSearchParams().get('token');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<{ tone: 'error' | 'success'; text: string } | null>(null);

  async function handleRequest(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    setMessage(null);

    const email = String(new FormData(event.currentTarget).get('email') ?? '');
    const { error } = await authClient.requestPasswordReset({ email, redirectTo: '/reset-password' });

    setSubmitting(false);
    if (error) {
      setMessage({ tone: 'error', text: error.message ?? 'Could not send the reset email.' });
      return;
    }
    setMessage({ tone: 'success', text: 'If that email has an account, a reset link is on its way.' });
  }

  async function handleConfirm(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!token) return;
    setSubmitting(true);
    setMessage(null);

    const newPassword = String(new FormData(event.currentTarget).get('password') ?? '');
    const { error } = await authClient.resetPassword({ newPassword, token });

    setSubmitting(false);
    if (error) {
      setMessage({ tone: 'error', text: error.message ?? 'Could not reset your password.' });
      return;
    }
    router.push('/login');
  }

  return (
    <main className="mx-auto flex max-w-md flex-1 flex-col justify-center px-4 py-16">
      <Card>
        <h1 className="mb-6 text-2xl font-semibold text-text-primary">
          {token ? 'Choose a new password' : 'Reset your password'}
        </h1>

        {token ? (
          <form onSubmit={handleConfirm} className="flex flex-col gap-4" noValidate>
            <TextField
              label="New password"
              name="password"
              type="password"
              autoComplete="new-password"
              minLength={8}
              required
            />
            {message && <FormMessage tone={message.tone}>{message.text}</FormMessage>}
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Saving…' : 'Set new password'}
            </Button>
          </form>
        ) : (
          <form onSubmit={handleRequest} className="flex flex-col gap-4" noValidate>
            <TextField label="Email" name="email" type="email" autoComplete="email" required />
            {message && <FormMessage tone={message.tone}>{message.text}</FormMessage>}
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Sending…' : 'Send reset link'}
            </Button>
          </form>
        )}

        <p className="mt-6 text-sm text-text-muted">
          <Link href="/login" className="text-text-link underline">
            Back to sign in
          </Link>
        </p>
      </Card>
    </main>
  );
}

export default function ResetPasswordPage() {
  return (
    <Suspense>
      <ResetPasswordForm />
    </Suspense>
  );
}
